import React, { useState } from 'react';
import { toast } from 'react-toastify';

// Material UI
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Components
import WarehouseReorderForm from './WarehouseReorderForm';

// Services
import * as warehouseReorderService from '../services/warehouseReorderService';

const WarehouseReorderList = ({ partId, warehouseReorders, onReorderChange }) => {
  const [openForm, setOpenForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedReorder, setSelectedReorder] = useState(null);
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'

  const handleAddClick = () => {
    setSelectedReorder(null);
    setFormMode('add');
    setOpenForm(true);
  };

  const handleEditClick = (reorder) => {
    setSelectedReorder(reorder);
    setFormMode('edit');
    setOpenForm(true);
  };

  const handleDeleteClick = (reorder) => {
    setSelectedReorder(reorder);
    setOpenDeleteDialog(true);
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setSelectedReorder(null);
  };

  const handleFormSuccess = () => {
    setOpenForm(false);
    setSelectedReorder(null);
    if (onReorderChange) {
      onReorderChange();
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await warehouseReorderService.deleteWarehouseReorder(selectedReorder.id);
      toast.success('Warehouse reorder point deleted successfully');
      setOpenDeleteDialog(false);
      setSelectedReorder(null);
      if (onReorderChange) {
        onReorderChange();
      }
    } catch (error) {
      console.error('Error deleting warehouse reorder point:', error);
      toast.error(error.message || 'Failed to delete warehouse reorder point');
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setSelectedReorder(null);
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="Warehouse-Specific Reorder Points"
        action={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            size="small"
          >
            Add Reorder Point
          </Button>
        }
      />
      <CardContent>
        {warehouseReorders && warehouseReorders.length > 0 ? (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Warehouse</TableCell>
                  <TableCell align="right">Reorder Point</TableCell>
                  <TableCell align="right">Target Stock Level</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {warehouseReorders.map((reorder) => (
                  <TableRow key={reorder.id}>
                    <TableCell>{reorder.warehouse.name}</TableCell>
                    <TableCell align="right">{reorder.reorder_point}</TableCell>
                    <TableCell align="right">
                      {reorder.target_stock_level || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditClick(reorder)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(reorder)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No warehouse-specific reorder points set. 
            {partId && (
              <Box component="span" sx={{ ml: 1 }}>
                Global reorder point will be used if available.
              </Box>
            )}
          </Typography>
        )}
      </CardContent>

      {/* Add/Edit Form Dialog */}
      <Dialog
        open={openForm}
        onClose={handleFormClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <WarehouseReorderForm
            reorderId={formMode === 'edit' ? selectedReorder?.id : null}
            partId={partId}
            onSuccess={handleFormSuccess}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Warehouse Reorder Point</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the reorder point for{' '}
            {selectedReorder?.warehouse?.name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default WarehouseReorderList;
